import React, { useState, useEffect } from "react";
import { Trash, EditIcon, AddIcon, TrashWhite } from "../../assets/icons/icons";
import CustomAlert from "../CustomAlert";
import Pagination from "../section/Pagination";
import axios from "axios";

export default function AddSubCategory() {
  // State Management
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_BASE = "https://laravel-api.aj-mart.com/api";

  // Form State
  const [formData, setFormData] = useState({
    sub_category_name_E: "",
    sub_category_name_A: "",
    sub_category_main_name: "",
    sub_category_img: ""
  });

  // Error and Alert States
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  // Dialog Confirmation States
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);
  // Initial Data Fetch
  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
  }, []);

  // Reset Form
  const resetForm = () => {
    setFormData({
      sub_category_name_E: "",
      sub_category_name_A: "",
      sub_category_main_name: "",
      sub_category_img: ""
    });
    setPreview(null);
    setImage(null);
    setErrors({});
  };

  // Alert Helper
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "", message: "" }), 5000);
  };

  // API Functions using axios
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/categories`);
      if (response.data && Array.isArray(response.data.category)) {
        setCategories(response.data.category);
      } else {
        setCategories([]);
        showAlert("error", "Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
      showAlert("error", "Error loading categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/subCategory`);
      if (response.data) {
        setSubCategories(response.data.sub_category);
      } else {
        showAlert("error", "Failed to fetch sub-categories");
      }
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
      showAlert("error", "Error loading sub-categories");
    } finally {
      setLoading(false);
    }
  };

  // Form Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.sub_category_name_E.trim()) {
      newErrors.sub_category_name_E = "English name is required";
    }
    if (!formData.sub_category_name_A.trim()) {
      newErrors.sub_category_name_A = "Arabic name is required";
    }
    if (!formData.sub_category_main_name) {
      newErrors.sub_category_main_name = "Main category is required";
    }
    if (!formData.sub_category_img && !editData) {
      newErrors.sub_category_img = "Image is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Image Handling
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setFormData((prev) => ({
          ...prev,
          sub_category_img: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // CRUD Operations using axios
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const formDataToSend = new FormData();
    formDataToSend.append("sub_category_name_E", formData.sub_category_name_E);
    formDataToSend.append("sub_category_name_A", formData.sub_category_name_A);
    formDataToSend.append("sub_category_main_name", formData.sub_category_main_name);
    if (image) formDataToSend.append("sub_category_img", image);

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/subCategory/add`, formDataToSend);
      if (response.data) {
        showAlert("success", "Sub-category added successfully");
        resetForm();
        await fetchSubCategories();
      } else {
        showAlert("error", "Failed to add sub-category");
      }
    } catch (error) {
      console.error("Error adding sub-category:", error);
      showAlert("error", "An error occurred while adding the sub-category");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append("_method", "PUT");
      formDataToSend.append("sub_category_name_E", formData.sub_category_name_E);
      formDataToSend.append("sub_category_name_A", formData.sub_category_name_A);
      formDataToSend.append("sub_category_main_name", formData.sub_category_main_name);
      if (image) {
        formDataToSend.append("sub_category_img", image);
      }
      const response = await axios.post(
        `${API_BASE}/subCategory/update/${editData.sub_category_id}`,
        formDataToSend
      );
      if (response.data) {
        showAlert("success", "Sub-category updated successfully");
        setShowModal(false);
        resetForm();
        await fetchSubCategories();
      } else {
        showAlert("error", "Failed to update sub-category");
      }
    } catch (error) {
      console.error("Error updating sub-category:", error);
      showAlert("error", "An error occurred while updating the sub-category");
    } finally {
      setLoading(false);
    }
  };

  // Delete Operations using axios with custom dialog
  const handleDelete = (id) => {
    // بدلاً من window.confirm، نقوم بتعيين state لإظهار الـ dialog
    setDeleteItemId(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_BASE}/subCategory/delete/${deleteItemId}`);
      if (response.data) {
        showAlert("success", "Sub-category deleted successfully");
        await fetchSubCategories();
      } else {
        showAlert("error", "Failed to delete sub-category");
      }
    } catch (error) {
      console.error("Error deleting sub-category:", error);
      showAlert("error", "An error occurred while deleting the sub-category");
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
      setDeleteItemId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedItems.length) {
      showAlert("error", "Please select items to delete");
      return;
    }
    setShowBulkDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setLoading(true);
      // نفترض أنه لكل عنصر يتم استدعاء endpoint الحذف
      const deletePromises = selectedItems.map((id) =>
        axios.delete(`${API_BASE}/subCategory/delete/${id}`)
      );
      await Promise.all(deletePromises);
      showAlert("success", "Selected sub-categories deleted successfully");
      setSelectedItems([]);
      await fetchSubCategories();
    } catch (error) {
      console.error("Error deleting sub-categories:", error);
      showAlert("error", "An error occurred while deleting sub-categories");
    } finally {
      setLoading(false);
      setShowBulkDeleteDialog(false);
    }
  };

  // Selection Handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(subCategories.map((item) => item.sub_category_id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <section className="py-6 flex flex-col gap-4">
      {alert.show && (
        <CustomAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ show: false, type: "", message: "" })}
        />
      )}

      <div className="main-title mb-5">Add Sub Category</div>

      {/* Add Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-black">Sub Category name In English</label>
            <input
              type="text"
              className={`px-4 py-2 outline-none border-2 ${
                errors.sub_category_name_E ? "border-red-500" : "border-[#1485a6]"
              } rounded bg-[#eeeeee6e]`}
              value={formData.sub_category_name_E}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  sub_category_name_E: e.target.value
                }))
              }
              placeholder="Sub Category Name In English"
            />
            {errors.sub_category_name_E && (
              <span className="text-red-500 text-sm">{errors.sub_category_name_E}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-black">Sub Category name In Arabic</label>
            <input
              type="text"
              className={`px-4 py-2 outline-none border-2 ${
                errors.sub_category_name_A ? "border-red-500" : "border-[#1485a6]"
              } rounded bg-[#eeeeee6e]`}
              value={formData.sub_category_name_A}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  sub_category_name_A: e.target.value
                }))
              }
              placeholder="Sub Category Name In Arabic"
            />
            {errors.sub_category_name_A && (
              <span className="text-red-500 text-sm">{errors.sub_category_name_A}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-black">Main Category</label>
            <select
              className={`px-4 py-2 outline-none border-2 ${
                errors.sub_category_main_name ? "border-red-500" : "border-[#1485a6]"
              } rounded bg-[#eeeeee6e]`}
              value={formData.sub_category_main_name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  sub_category_main_name: e.target.value
                }))
              }
            >
              <option value="">Select Main Category</option>
              {categories.map((category) => (
                <option key={category.category_id} value={category.category_name_E}>
                  {category.category_name_E}
                </option>
              ))}
            </select>
            {errors.sub_category_main_name && (
              <span className="text-red-500 text-sm">{errors.sub_category_main_name}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-black">Sub Category Image</label>
            <div className="flex items-center gap-4">
              <label
                htmlFor="upload-image"
                className="cursor-pointer border-2 border-[#1485a6] bg-[#eeeeee6e] text-black px-4 py-2 rounded flex items-center justify-center"
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="h-10 w-10 object-cover rounded" />
                ) : (
                  "Upload Image"
                )}
              </label>
              <input
                id="upload-image"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </div>
            {errors.sub_category_img && (
              <span className="text-red-500 text-sm">{errors.sub_category_img}</span>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="btn flex items-center gap-2 bg-[#1485a6] text-white px-4 py-2 rounded"
          disabled={loading}
        >
          <AddIcon />
          <span>{loading ? "Adding..." : "Add Sub Category"}</span>
        </button>
      </form>

      {/* Bulk Delete Button */}
      {selectedItems.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleBulkDelete}
            className="btn bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded flex items-center gap-2"
            disabled={loading}
          >
            <TrashWhite />
            <span>Delete Selected ({selectedItems.length})</span>
          </button>
        </div>
      )}

      {/* Sub Categories Table */}
      <div className="overflow-x-auto mt-6">
        <table className="w-full border-collapse border border-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-200 p-3">
                <input
                  type="checkbox"
                  className="w-[18px] h-[18px]"
                  onChange={handleSelectAll}
                  checked={selectedItems.length === subCategories.length}
                />
              </th>
              <th className="border border-gray-200 p-3">Image</th>
              <th className="border border-gray-200 p-3">Category Name</th>
              <th className="border border-gray-200 p-3">Sub Category Name</th>
              <th className="border border-gray-200 p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subCategories.map((item) => (
              <tr key={item.sub_category_id} className="bg-white hover:bg-gray-50">
                <td className="border border-gray-200 p-3">
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px]"
                    checked={selectedItems.includes(item.sub_category_id)}
                    onChange={() => handleSelectItem(item.sub_category_id)}
                  />
                </td>
                <td className="border border-gray-200 p-3">
                  <img
                    src={`https://laravel-api.aj-mart.com/storage/app/public/${item.sub_category_img}`}
                    alt={item.sub_category_name_E}
                    className="w-10 h-10 object-cover rounded"
                  />
                </td>
                <td className="border border-gray-200 p-3">{item.sub_category_main_name}</td>
                <td className="border border-gray-200 p-3">
                  <div>
                    <div>{item.sub_category_name_E}</div>
                    <div className="text-sm text-gray-500">{item.sub_category_name_A}</div>
                  </div>
                </td>
                <td className="border border-gray-200 p-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setEditData(item);
                        setFormData({
                          sub_category_name_E: item.sub_category_name_E,
                          sub_category_name_A: item.sub_category_name_A,
                          sub_category_main_name: item.sub_category_main_name,
                          sub_category_img: item.sub_category_img
                        });
                        setPreview(item.sub_category_img);
                        setShowModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                      disabled={loading}
                    >
                      <EditIcon />
                    </button>
                    <button
                      onClick={() => handleDelete(item.sub_category_id)}
                      className="text-red-600 hover:text-red-800"
                      disabled={loading}
                    >
                      <Trash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="py-6">
        <Pagination />
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[500px] rounded-lg shadow-lg p-5 relative">
            <h2 className="text-xl font-bold mb-4">Edit Sub Category</h2>
            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-black">Sub Category name In English</label>
                <input
                  type="text"
                  className={`px-4 py-2 outline-none border-2 ${
                    errors.sub_category_name_E ? "border-red-500" : "border-[#1485a6]"
                  } rounded bg-[#eeeeee6e]`}
                  value={formData.sub_category_name_E}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sub_category_name_E: e.target.value
                    }))
                  }
                />
                {errors.sub_category_name_E && (
                  <span className="text-red-500 text-sm">{errors.sub_category_name_E}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-black">Sub Category name In Arabic</label>
                <input
                  type="text"
                  className={`px-4 py-2 outline-none border-2 ${
                    errors.sub_category_name_A ? "border-red-500" : "border-[#1485a6]"
                  } rounded bg-[#eeeeee6e]`}
                  value={formData.sub_category_name_A}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sub_category_name_A: e.target.value
                    }))
                  }
                />
                {errors.sub_category_name_A && (
                  <span className="text-red-500 text-sm">{errors.sub_category_name_A}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-black">Main Category</label>
                <select
                  className={`px-4 py-2 outline-none border-2 ${
                    errors.sub_category_main_name ? "border-red-500" : "border-[#1485a6]"
                  } rounded bg-[#eeeeee6e]`}
                  value={formData.sub_category_main_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sub_category_main_name: e.target.value
                    }))
                  }
                >
                  <option value="">Select Main Category</option>
                  {categories.map((category) => (
                    <option key={category.category_id} value={category.category_name_E}>
                      {category.category_name_E}
                    </option>
                  ))}
                </select>
                {errors.sub_category_main_name && (
                  <span className="text-red-500 text-sm">{errors.sub_category_main_name}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-black">Sub Category Image</label>
                <div className="flex items-center gap-4">
                  <label
                    htmlFor="edit-image"
                    className="cursor-pointer border-2 border-[#1485a6] bg-[#eeeeee6e] text-black px-4 py-2 rounded flex items-center justify-center"
                  >
                    Change Image
                  </label>
                  <input
                    id="edit-image"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  {preview && (
                    <img src={preview} alt="Preview" className="h-10 w-10 object-cover rounded" />
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                className="btn bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                onClick={handleUpdate}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog for Single Delete */}
      {showDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this sub-category?</p>
            <div className="flex justify-center gap-4">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowDeleteDialog(false)}
              >
                No
              </button>
              <button className="btn bg-red-500 hover:bg-red-600" onClick={confirmDelete}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog for Bulk Delete */}
      {showBulkDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Bulk Deletion</h2>
            <p className="mb-4">
              Are you sure you want to delete {selectedItems.length} sub-categories?
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowBulkDeleteDialog(false)}
              >
                Cancel
              </button>
              <button className="btn bg-red-500 hover:bg-red-600" onClick={confirmBulkDelete}>
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
